import { type ReactNode } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Tier } from '../hooks/use-pricing-table';
import { TierRange, TierPrice } from './pricing-tier-inputs';
import { DragHandle, RemoveButton } from './pricing-tier-actions';

type Props = {
  id: string;
  tier: Tier;
  isFirst: boolean;
  isLast: boolean;
  canRemove: boolean;
  hasError?: boolean;
  priceError?: boolean;
  onChange: (t: Tier) => void;
  onRemove: () => void;
};

function RowContent(p: Props): ReactNode {
  return (
    <>
      <TierRange
        tier={p.tier}
        onChange={p.onChange}
        isFirst={p.isFirst}
        isLast={p.isLast}
        hasError={p.hasError ?? false}
      />
      <TierPrice
        tier={p.tier}
        onChange={p.onChange}
        hasError={p.priceError ?? false}
      />
      <RemoveButton canRemove={p.canRemove} onRemove={p.onRemove} />
    </>
  );
}

function useTierSortable(id: string, locked: boolean) {
  const s = useSortable({
    id,
    animateLayoutChanges: () => false,
    disabled: locked,
  });
  const style = locked
    ? undefined
    : {
        transform: CSS.Transform.toString(s.transform),
        transition: s.transition,
      };
  return { ...s, style };
}

export function PricingTierRow(p: Props): ReactNode {
  const locked = p.isFirst || p.isLast;
  const { attributes, listeners, setNodeRef, style, isDragging } =
    useTierSortable(p.id, locked);
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex w-fit items-center gap-sm rounded-md px-1 py-1 ${isDragging ? 'opacity-30' : ''}`}
    >
      <DragHandle
        locked={locked}
        listeners={listeners}
        attributes={attributes}
      />
      <RowContent {...p} />
    </div>
  );
}
